import React from 'react';
import { View, TouchableOpacity, Image } from 'react-native';
import MyAppText from '../MyAppText';
import { translate } from '../../services/translate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { WindowInformation } from '../../services/window-information';
import { InfoInRow } from '../InfoInRow';
import { faCalendarCheck, faLocationDot, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import Card from '../Card';
import { RootNavigation } from '../../Routes';
import { openInNewTab } from '../../services/navigation-assistant';

export function MpPlateDetectedOccurrence({ occurrence, navigation, imageUrl }: { occurrence: Occurrence; navigation: RootNavigation; imageUrl: string; }) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    if (!occurrence.resume) {
        return <></>;
    }
    const resume = occurrence.resume as MpPlateDetectedResumeData;

    return (
        <View style={{ gap: 20 }}>
            <View style={{ gap: 20 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <TouchableOpacity
                            onPress={() => {
                                if (occurrence.id) {
                                    openInNewTab('Dispatch', { screen: 'DispatchOccurrenceDetail', params: { occurrenceId: occurrence.id } });
                                }
                            }}
                            disabled={windowInfo.isMobile}
                        >
                            <MyAppText style={{ fontSize: 16, color: theme.colors.textColor, fontWeight: 'bold' }}>
                                {translate('nProtocol')}: {occurrence?.id}
                            </MyAppText>
                        </TouchableOpacity>
                        <MyAppText> </MyAppText>
                        <MyAppText style={{ fontSize: 14, color: theme.colors.textColor }}>
                            ({translate('team')} {translate(occurrence.OccurrenceUnits?.length && occurrence.OccurrenceUnits[0].OccurrenceUnit.isStarter ? 'starter' : 'support').toLowerCase()})
                        </MyAppText>
                    </View>
                    <TouchableOpacity onPress={() => navigation.navigate('AppAgent', { screen: 'AgentOccurrences' })}>
                        <FontAwesomeIcon color={theme.colors.iconColor} fontSize={30} icon={faTimes as IconDefinition} />
                    </TouchableOpacity>
                </View>
                <MyAppText style={styles.alertText}>
                    {translate('mpPlateDetectedAlert')}:
                </MyAppText>
                <MyAppText style={styles.alertName}>{resume.plate}</MyAppText>
                <View style={styles.imageBox}>
                    <Image source={{ uri: imageUrl }} style={styles.image} />
                </View>
            </View>
            <Card>
                <View style={{ gap: 3 }}>
                    <MyAppText style={styles.cardBoldText}>
                        {translate('vehicleData')}
                    </MyAppText>
                    <>
                        <InfoInRow title={'brand'} value={resume.modelBrand} />
                        <InfoInRow title={'brandCode'} value={resume.brandCode} />
                        <InfoInRow title={'color'} value={resume.color} />
                        <InfoInRow title={'colorCode'} value={resume.colorCode} />
                        <InfoInRow title={'denatranCode'} value={resume.denatranCode} />
                        <InfoInRow title={'origin'} value={`${resume.plateMunicipality}/${resume.plateFederativeUnit}`} />
                        <InfoInRow title={'manufacturingYear'} value={resume.manufacturingYear} />
                        <InfoInRow title={'modelYear'} value={resume.modelYear} />
                        <InfoInRow title={'origination'} value={resume.origin} />
                        <InfoInRow title={'chassis'} value={resume.chassis} />
                    </>
                </View>
                <View style={{ gap: 3 }}>
                    <MyAppText style={styles.cardBoldText}>
                        {translate('visualizedOn')}
                    </MyAppText>
                    <>
                        <InfoInRow value={resume.time} isDate={true} fontAwesomeIcon={faCalendarCheck} />
                        <InfoInRow value={`${resume.location} / ${resume.federativeUnit}`} fontAwesomeIcon={faLocationDot} />
                        <InfoInRow title={'city'} value={resume.city} />
                        <InfoInRow title={'federativeUnit'} value={resume.federativeUnit} />
                    </>
                </View>
                <InfoInRow title={'description'} value={resume.occurrenceHistory} />
                <View style={{ gap: 3 }}>
                    <MyAppText style={styles.cardBoldText}>{translate('systemData')}</MyAppText>
                    <>
                        <InfoInRow title={'occurrenceSystem'} value={resume.occurrenceSystem} />
                        <InfoInRow title={'natureOfOccurrence'} value={resume.natureOfOccurrence} />
                        <InfoInRow title={'policeReportNumber'} value={resume.policeReportNumber} />
                        <InfoInRow title={'policeReportYear'} value={resume.policeReportYear} />
                        <InfoInRow title={'policeReportLocation'} value={`${resume.policeReportMunicipality}/${resume.policeReportFederativeUnit}`} />
                        <InfoInRow title={'declarantName'} value={resume.declarantName} />
                        {resume.contactAreaCode && resume.contactPhone ?
                            <InfoInRow title={'phone'} value={`(${resume.contactAreaCode}) ${resume.contactPhone} ${resume.contactBranch}`} /> : null}
                        <InfoInRow title={'occurrenceDate'} value={moment(new Date(resume.occurrenceDate)).format('DD/MM/YYYY HH:mm:ss')} />
                        <InfoInRow title={'policeUnit'} value={resume.policeUnit} />
                        <InfoInRow title={'readerIdentifier'} value={resume.readerIdentifier} />
                        <InfoInRow title={'readerDescription'} value={resume.readerDescription} />
                        <InfoInRow title={'readerInstitutionDescription'} value={resume.readerInstitutionDescription} />
                        <InfoInRow title={'institutionRegistryDateTime'} value={resume.institutionRegistryDateTime} isDate={true} />
                    </>
                </View>
                <View>
                    <InfoInRow title={'priority'} valueStyle={occurrence.priority === 'maximum' ? { color: '#FF0027' } : undefined} value={occurrence.priority ? translate(occurrence.priority) : ''} />
                    <InfoInRow title={'narrative'} value={occurrence.narrative} />
                </View>
            </Card>
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    alertText: {
        color: theme.colors.textColor,
        fontSize: 16,
        fontWeight: 'bold',
    },
    cardBoldText: {
        color: theme.colors.textColor,
        fontWeight: 'bold',
        fontSize: 14
    },
    alertName: {
        color: theme.colors.textColor,
        fontSize: 18,
        fontWeight: 'bold',
    },
    imageBox: {
        height: 314,
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
    },
    image: {
        height: 314,
        width: 311,
        objectFit: 'contain',
        borderRadius: 8,
        resizeMode: 'contain',
        overflow: 'hidden',
    },
}));
